import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { idMapToArray, ScopeService } from './scope.service';
import { UtilsService } from './utils.service';

export interface PerimeterReferenceDataMap extends KPI.Perimeter {
  ref_data_map: { [varName: string]: KPI.PerimeterReferenceValue };
  sub_perimeters_ref_data_map: PerimeterReferenceDataMap[];
}

@Injectable()
export class ReferenceDataService {
  private _perimeters$: Observable<KPI.Perimeter[]>;
  private _perimeterReferenceDataMaps$: Observable<PerimeterReferenceDataMap[]>;
  private _perimeterReferenceValues$: {[key: string]: Observable<KPI.PerimeterReferenceValue[]>} = {};
  private _year$ = new BehaviorSubject<number>(new Date().getFullYear());
  private perimeterReferenceValuesRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private scopeService: ScopeService,
  ) { }

  public perimeterReferenceValues$(params?: {years?: string, perimeter_ids: string}): Observable<
    KPI.PerimeterReferenceValue[]
  > {
    const key = UtilsService.getKey(arguments);
    if (!this._perimeterReferenceValues$[key]) {
      this._perimeterReferenceValues$[key] =
        this.perimeterReferenceValuesRefresh$.pipe(
          switchMap(() => this.api.reference_data_perimeter_reference_values(params)),
          shareReplay(1),
        );
    }

    return this._perimeterReferenceValues$[key];
  }

  /**
   * Forces refresh of perimeterReferenceValues
   */
  public refreshRerimeterReferenceValues(): void {
    this.perimeterReferenceValuesRefresh$.next();
  }

  public get perimeters$(): Observable<KPI.Perimeter[]> {
    if (!this._perimeters$) {
      this._perimeters$ = this.scopeService.perimeters$.pipe(
        idMapToArray(),
        map((perimeters) => {
          return perimeters.map((perimeter) => {
            return ScopeService.mapPerimetersFilterRefData([perimeter])[0];
          });
        }),
        shareReplay(1),
      );
    }

    return this._perimeters$;
  }

  public get perimeterReferenceDataMaps$(): Observable<
    PerimeterReferenceDataMap[]
  > {
    if (!this._perimeterReferenceDataMaps$) {
      this._perimeterReferenceDataMaps$ = this.perimeters$.pipe(
        map((perimeters) =>
          perimeters.map((perimeter) =>
            this.mapPerimeterReferenceData(perimeter),
          ),
        ),
        shareReplay(1),
      );
    }

    return this._perimeterReferenceDataMaps$;
  }

  /**
   * Recover perimeter with value of reference datas for the selected year
   */
  public get perimeterReferenceDataMapYear$(): Observable<
    PerimeterReferenceDataMap[]
  > {
    return combineLatest(
      this.scopeService.perimeters$.pipe(idMapToArray()),
      this.perimeterReferenceValues$(),
      this.year$,
    ).pipe(
      map(([perimeters, referenceValues, year]) => {
        return perimeters.map((perimeter) => {
          perimeter = this.mapRefData(perimeter, referenceValues);
          const permiFilter = ScopeService.mapPerimetersFilterRefData([
            perimeter,
          ])[0];
          return this.mapPerimeterReferenceData(permiFilter, year);
        });
      }),
    );
  }

  /**
   * Get the selected year
   */
  public get year$(): Observable<number> {
    return this._year$;
  }

  /**
   * Update the selected year
   *
   * @param year
   */
  public updateYear(year: number): void {
    this._year$.next(year);
  }

  /**
   * Calls the api to create a PerimeterReferenceValue
   *
   * @param perimeterReferenceValue
   */
  public createPerimeterReferenceValue(
    perimeterReferenceValue: Partial<KPI.PerimeterReferenceValue>,
  ): Observable<KPI.PerimeterReferenceValue> {
    return this.api
      .reference_data_perimeter_reference_values_post(perimeterReferenceValue)
      .pipe(
        tap(() => this.refreshRerimeterReferenceValues()),
      );
  }

  /**
   * Calls the api to update a PerimeterReferenceValue
   *
   * @param id
   * @param perimeterReferenceValue
   */
  public updatePerimeterReferenceValue(
    id: number,
    perimeterReferenceValue: Partial<KPI.PerimeterReferenceValue>,
  ): Observable<KPI.PerimeterReferenceValue> {
    return this.api
      .reference_data_perimeter_reference_values_patch(
        id,
        perimeterReferenceValue,
      )
      .pipe(
        tap(() => this.refreshRerimeterReferenceValues()),
      );
  }

  private mapRefData(
    perimeter: KPI.Perimeter,
    referenceValues: KPI.PerimeterReferenceValue[],
  ): KPI.Perimeter {
    const refValues = referenceValues.filter(
      (values) => values.perimeter === perimeter.id,
    );
    if (!perimeter.ref_data) {
      perimeter.ref_data = [];
    }
    perimeter.ref_data = perimeter.ref_data.concat(refValues);
    return perimeter;
  }

  private mapPerimeterReferenceData(
    perimeter: KPI.Perimeter,
    year?: number,
  ): PerimeterReferenceDataMap {
    let refDataPerim = perimeter.ref_data;
    if (year) {
      refDataPerim = refDataPerim.filter((data) => data.year === year);
    }
    return {
      ...perimeter,
      ref_data_map: refDataPerim
        ? refDataPerim.reduce(
          (refDataMap, refData) => ({
            ...refDataMap,
            [refData.var_name]: refData,
          }),
          {},
        )
        : null,
      sub_perimeters_ref_data_map:
        (perimeter.sub_perimeters &&
          perimeter.sub_perimeters.map((subPerimeter) =>
            this.mapPerimeterReferenceData(subPerimeter, year),
          )) ||
        [],
    };
  }

  /**
   * Calls the api to create a ReferenceField
   *
   * @param referenceField
   */
  public createReferenceField(
    referenceField: Partial<KPI.ReferenceField>,
  ): Observable<KPI.ReferenceField> {
    return this.api
      .reference_field_post(referenceField).pipe(
        tap(() => this.scopeService.refreshReferenceFields()),
      );
  }

  public updateReferenceFields(id: number, field: Partial<KPI.ReferenceField>): Observable<KPI.ReferenceField> {
    return this.api.reference_field_patch(id, field).pipe(tap(() => this.scopeService.refreshReferenceFields()))
  }

  // Check if ref data form in site sheet has pending changes
  private anyValueChangedFromInitialSubject = new BehaviorSubject<boolean>(false);
  public anyValueChangedFromInitial$ = this.anyValueChangedFromInitialSubject.asObservable();

  setValueChanged(changed: boolean) {
    this.anyValueChangedFromInitialSubject.next(changed);
  }

}
